// src/components/crm/StageInspectorDrawer.jsx
import { useEffect, useState } from "react";
import { Modal, Button, Select, TextInput } from "flowbite-react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { CUSTOM_FIELDS, CRM } from "../../config/apiConfig";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const AUTO_LEAD_STATUS = [
  { value: "", label: "— Não alterar —" },
  { value: "working", label: "Em Progresso" },
  { value: "qualified", label: "Qualificado" },
  { value: "unqualified", label: "Não Qualificado" },
  { value: "converted", label: "Convertido" },
];

// helper simples de classes
const cx = (...c) => c.filter(Boolean).join(" ");

// Normaliza o retorno do endpoint de required-fields (com prioridade)
function normalizeRequiredFields(raw) {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((row, idx) => {
      // payload esperado: { customFieldId, priority }
      if (row && (row.customFieldId || row.id)) {
        return {
          id: String(row.customFieldId || row.id),
          priority: Number(row.priority) || idx + 1,
        };
      }

      if (typeof row === "string" || typeof row === "number") {
        return { id: String(row), priority: idx + 1 };
      }

      return null;
    })
    .filter(Boolean)
    .sort((a, b) => (a.priority || 0) - (b.priority || 0));
}

export default function StageInspectorDrawer({
  open,
  stage,
  onClose,
  onPatch, // (patchObj) => void (aplica no estado local do pai)
  onRename, // (newName) => void  (aplica no estado local do pai)
}) {
  const stageId = stage?.id;
  const funnelId = stage?.funnelId;

  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [selectedFieldIdToAdd, setSelectedFieldIdToAdd] = useState("");
  // array [{id, priority}]
  const [requiredEntries, setRequiredEntries] = useState([]);
  const [savingRequiredFields, setSavingRequiredFields] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // ====== Custom fields disponíveis do funil ======
  const { data: customFields = [], isLoading: loadingCustomFields } = useQuery(
    ["stage-custom-fields", funnelId],
    async () => {
      const res = await axios.get(CUSTOM_FIELDS.LIST(funnelId));
      const data = res?.data?.data || res?.data || [];
      return Array.isArray(data) ? data : [];
    },
    {
      enabled: !!funnelId && open,
      initialData: [],
    }
  );

  const {
    data: requiredFieldsFromApi = [],
    isLoading: loadingRequiredFields,
  } = useQuery(
    ["stage-required-fields", stageId],
    async () => {
      const res = await axios.get(
        `${CRM.STAGE_REQUIRED_FIELDS}/${stageId}/required-fields`
      );
      const raw = res?.data?.data ?? res?.data ?? [];
      return normalizeRequiredFields(raw);
    },
    {
      enabled: !!stageId && open,
      initialData: [],
    }
  );

  // ====== Função central de salvar (auto-save) ======
  const saveRequiredFields = async (entries) => {
    if (!stageId) return;

    const payload =
      entries && entries.length
        ? entries.map((row, idx) => ({
            customFieldId: String(row.id),
            priority: Number(row.priority) || idx + 1,
          }))
        : [];

    try {
      setSavingRequiredFields(true);
      await axios.put(
        `${CRM.STAGE_REQUIRED_FIELDS}/${stageId}/required-fields`,
        {
          requiredFields: payload,
        }
      );
      console.log("Campos obrigatórios salvos para a etapa:", payload);
    } catch (error) {
      console.error("Erro ao salvar campos obrigatórios da etapa:", error);
    } finally {
      setSavingRequiredFields(false);
    }
  };

  // helper pra aplicar mudança + auto-save
const applyAndSaveRequirements = (updater) => {
  setRequiredEntries((prev) => {
    const base = Array.isArray(prev) ? prev : [];
    const next =
      typeof updater === "function" ? updater(base) : updater || base;
    const normalized = (next || [])
      .map((row, idx) => ({
        id: String(row.id),
        priority: Number(row.priority) || idx + 1,
      }))
      .sort((a, b) => a.priority - b.priority)
      .map((row, idx) => ({ ...row, priority: idx + 1 }));

    saveRequiredFields(normalized);
    if (typeof onPatch === "function") {
      onPatch({
        requiredFields: normalized.map((row) => ({
          customFieldId: row.id,
          priority: row.priority,
        })),
      });
    }
    return normalized;
  });
};

  // Sincroniza state local quando abrir / mudar dados
  useEffect(() => {
    if (!open) return;

    if (!customFields || !customFields.length) {
      setRequiredEntries([]);
      return;
    }

    const fromApi =
      (requiredFieldsFromApi || []).map((row, idx) => ({
        id: String(row.id),
        priority: Number(row.priority) || idx + 1,
      })) || [];

    // se o backend não tiver nada, limpa
    if (!fromApi.length) {
      setRequiredEntries([]);
      return;
    }

    setRequiredEntries((prev) => {
      const prevKey = (prev || []).map((r) => `${r.id}:${r.priority}`).join("|");
      const nextKey = fromApi.map((r) => `${r.id}:${r.priority}`).join("|");
      if (prevKey === nextKey) return prev || [];
      return fromApi.sort((a, b) => a.priority - b.priority);
    });
  }, [open, customFields, requiredFieldsFromApi]);

  if (!open || !stage) return null;

  const stageType = stage?.isWin ? "win" : stage?.isLost ? "lost" : "normal";

  const handleStageTypeChange = (value) => {
    if (value === "win") {
      onPatch({ isWin: true, isLost: false });
    } else if (value === "lost") {
      onPatch({ isWin: false, isLost: true });
    } else {
      onPatch({ isWin: false, isLost: false });
    }
  };

  const setNum = (k, v) => {
    const n = Number(v);
    if (Number.isFinite(n)) onPatch({ [k]: Math.max(0, Math.min(100, n)) });
  };

  const requiredIds = requiredEntries.map((r) => String(r.id));
  const requiredSet = new Set(requiredIds);

  const availableFieldsForSelect = customFields.filter(
    (f) => !requiredSet.has(String(f.id))
  );

  const handleOpenSelector = () => {
    setShowFieldSelector((prev) => !prev);
    setSelectedFieldIdToAdd("");
  };

  const handleSelectChange = (e) => {
    setSelectedFieldIdToAdd(e.target.value);
  };

  const handleAddFieldAsRequired = () => {
    if (!selectedFieldIdToAdd) return;

    applyAndSaveRequirements((prev) => [
      ...(prev || []),
      { id: String(selectedFieldIdToAdd), priority: (prev?.length || 0) + 1 },
    ]);
    setSelectedFieldIdToAdd("");
  };

  const handleRemoveFieldFromStage = (fieldId) => {
    const key = String(fieldId);
    applyAndSaveRequirements((prev) =>
      (prev || [])
        .filter((row) => String(row.id) !== key)
        .map((row, idx) => ({ ...row, priority: idx + 1 }))
    );
  };

  const handleReorder = (fieldId, newPriority) => {
    const targetPriority = Number(newPriority);
    if (!Number.isFinite(targetPriority) || targetPriority < 1) return;

    applyAndSaveRequirements((prev) => {
      const list = [...(prev || [])];
      const idx = list.findIndex((r) => String(r.id) === String(fieldId));
      if (idx === -1) return list;
      const [item] = list.splice(idx, 1);
      const clamped = Math.max(0, Math.min(list.length, targetPriority - 1));
      list.splice(clamped, 0, item);
      return list.map((row, i) => ({ ...row, priority: i + 1 }));
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    applyAndSaveRequirements((prev) => {
      const oldIndex = (prev || []).findIndex(
        (row) => String(row.id) === String(active.id)
      );
      const newIndex = (prev || []).findIndex(
        (row) => String(row.id) === String(over.id)
      );
      if (oldIndex === -1 || newIndex === -1) return prev || [];
      const reordered = arrayMove(prev, oldIndex, newIndex);
      return reordered.map((row, idx) => ({ ...row, priority: idx + 1 }));
    });
  };

  const loadingAny = loadingCustomFields || loadingRequiredFields;
  const requiredCustomFieldsList = requiredEntries
    .map((row) =>
      customFields.find((f) => String(f.id) === String(row.id))
        ? {
            ...customFields.find((f) => String(f.id) === String(row.id)),
            priority: row.priority,
          }
        : null
    )
    .filter(Boolean)
    .sort((a, b) => (a.priority || 0) - (b.priority || 0));

// item sortável para a lista de campos obrigatórios
function SortableFieldRow({
  field,
  index,
  total,
  onReorder,
  onRemove,
  disabled,
}) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(field.id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    cursor: disabled ? "not-allowed" : "grab",
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 px-3 py-2"
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex items-center gap-2">
          <div className="h-8 w-2 rounded-full bg-gray-300" />
          <Select
            value={field.priority}
            onChange={(e) => onReorder(field.id, e.target.value)}
            className="w-20 text-sm"
            disabled={disabled}
          >
            {Array.from({ length: total }).map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </Select>
        </div>
        <div className="min-w-0">
          <div className="text-sm text-gray-900 truncate">{field.label}</div>
          <p className="text-[11px] text-gray-500">
            Prioridade #{field.priority || index + 1}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => onRemove(field.id)}
          className="text-[11px] text-red-500 hover:text-red-700"
          disabled={disabled}
        >
          Remover
        </button>
      </div>
    </li>
  );
}

  return (
    <Modal show={open} onClose={onClose} size="7xl" className="!items-start">
      <div className="fixed inset-y-0 right-0 w-full sm:w-[560px] lg:w-[680px] bg-white shadow-2xl border-l border-gray-200 overflow-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Etapa do funil
            </div>
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {stage.name}
            </h3>
          </div>
          <div className="flex items-center gap-3">
            <Button color="light" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-10">
          {/* Identificação */}
          <section className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
              <div className="md:col-span-6">
                <label className="text-xs font-medium text-gray-600 block mb-1.5">
                  Nome da etapa
                </label>
                <TextInput
                  className="h-11"
                  value={stage.name || ""}
                  onChange={(e) => onRename(e.target.value)}
                  placeholder="Ex.: Qualificação"
                />
              </div>

              {/* Regras */}
              <section className="space-y-4 md:col-span-6">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">
                    Mudar automaticamente o "status"
                  </label>
                  <Select
                    className="h-11"
                    value={stage.autoLeadStatus || ""}
                    onChange={(e) =>
                      onPatch({ autoLeadStatus: e.target.value || null })
                    }
                  >
                    {AUTO_LEAD_STATUS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </section>

              <div className="md:col-span-6">
                <label className="text-xs font-medium text-gray-600 block mb-1.5">
                  Probabilidade de fechamento
                </label>
                <div className="flex items-center gap-2">
                  <TextInput
                    type="number"
                    min={0}
                    max={100}
                    className="h-11 w-full"
                    value={stage.probability ?? 0}
                    onChange={(e) => setNum("probability", e.target.value)}
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>

              <div className="md:col-span-6">
                <label className="text-xs font-medium text-gray-600 block mb-1.5">
                  Cor da etapa
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={stage.color || "#141414"}
                    onChange={(e) => onPatch({ color: e.target.value })}
                    className="h-11 w-full p-0 border rounded"
                  />
                  <span className="text-sm text-gray-600">
                    {stage.color || "#141414"}
                  </span>
                </div>
              </div>

              {/* Tipo de etapa: normal, ganho ou perdido (visual) */}
              <div className="md:col-span-12">
                <label className="text-xs font-medium text-gray-600 block mb-1.5">
                  Tipo da etapa
                </label>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap gap-3">
                    {/* Etapa padrão */}
                    <button
                      type="button"
                      onClick={() => handleStageTypeChange("normal")}
                      className={cx(
                        "flex-1 min-w-[140px] rounded-lg border px-3 py-2 text-left text-xs md:text-sm transition",
                        stageType === "normal"
                          ? "border-sky-500 bg-sky-50 shadow-sm"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-sky-500" />
                        <span className="font-semibold text-gray-800">
                          Etapa padrão
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] text-gray-500">
                        Leads ainda em andamento no funil.
                      </p>
                    </button>

                    {/* Etapa de ganho */}
                    <button
                      type="button"
                      onClick={() => handleStageTypeChange("win")}
                      className={cx(
                        "flex-1 min-w-[140px] rounded-lg border px-3 py-2 text-left text-xs md:text-sm transition",
                        stageType === "win"
                          ? "border-emerald-500 bg-emerald-50 shadow-sm"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        <span className="font-semibold text-gray-800">
                          Etapa de ganho
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] text-gray-500">
                        Marca os cards como <strong>ganhos</strong> e encerra o
                        fluxo neste funil.
                      </p>
                    </button>

                    {/* Etapa de perda */}
                    <button
                      type="button"
                      onClick={() => handleStageTypeChange("lost")}
                      className={cx(
                        "flex-1 min-w-[140px] rounded-lg border px-3 py-2 text-left text-xs md:text-sm transition",
                        stageType === "lost"
                          ? "border-rose-500 bg-rose-50 shadow-sm"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-rose-500" />
                        <span className="font-semibold text-gray-800">
                          Etapa de perda
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] text-gray-500">
                        Marca os cards como <strong>perdido</strong> e encerra o
                        fluxo neste funil.
                      </p>
                    </button>
                  </div>

                  <p className="mt-1 text-[11px] text-gray-500">
                    Use etapas de <span className="font-semibold">ganho</span> e{" "}
                    <span className="font-semibold">perda</span> para encerrar o
                    fluxo do lead nesse funil.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Form da etapa */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-sm font-semibold text-gray-800">
                Campos obrigatórios da etapa
              </h5>
              {stage?.id ? (
                <Link
                  to={`/crm/fields`}
                  className="text-sm text-indigo-600 hover:text-indigo-700"
                >
                  Gerenciar campos →
                </Link>
              ) : (
                <span className="text-xs text-gray-500">
                  Salve o funil para habilitar o formulário
                </span>
              )}
            </div>

            {/* Botão + select inline */}
            <div className="flex flex-col gap-2 mb-4">
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={handleOpenSelector}
                  className="px-4 py-1.5 bg-[#FE700B] text-white text-xs rounded-lg shadow-sm hover:bg-[#e46104] transition disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!stage?.id || !funnelId || loadingAny}
                >
                  {showFieldSelector ? "Fechar seleção" : "Adicionar campo"}
                </button>
                <span className="text-[11px] text-gray-500">
                  Marque quais campos precisam ser preenchidos para avançar
                  nesta etapa.
                </span>
              </div>

              {showFieldSelector && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 space-y-2">
                  <label className="text-xs font-medium text-gray-600 block">
                    Selecionar campos para marcar como obrigatórios
                  </label>
                  <div className="flex gap-2 items-center">
                    <Select
                      className="h-11 w-full text-sm"
                      value={selectedFieldIdToAdd}
                      onChange={handleSelectChange}
                      disabled={
                        loadingCustomFields || !availableFieldsForSelect.length
                      }
                    >
                      <option value="">
                        {loadingCustomFields
                          ? "Carregando campos..."
                          : availableFieldsForSelect.length
                          ? "Selecione um campo…"
                          : "Nenhum campo disponível"}
                      </option>
                      {availableFieldsForSelect.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.label}
                        </option>
                      ))}
                    </Select>
                    <Button
                      color="dark"
                      onClick={handleAddFieldAsRequired}
                      disabled={
                        !selectedFieldIdToAdd ||
                        loadingCustomFields ||
                        !availableFieldsForSelect.length ||
                        savingRequiredFields
                      }
                    >
                      {savingRequiredFields ? "Salvando..." : "Adicionar"}
                    </Button>
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Campos adicionados aqui aparecerão como{" "}
                    <span className="font-semibold text-amber-700">
                      obrigatórios
                    </span>{" "}
                    na lista abaixo.
                  </div>
                </div>
              )}
            </div>

            {/* Lista de campos */}
            {stage?.id && (
              <div className="rounded-md border border-gray-200 bg-white">
                <div className="px-3 py-2 border-b border-gray-200 text-xs text-gray-600 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span>Campo</span>
                  </div>
                  <span className="text-gray-500">
                    {requiredCustomFieldsList.length} campo(s)
                  </span>
                </div>

                <div className="p-3">
                  {loadingAny ? (
                    <p className="text-sm text-center text-gray-500">
                      Carregando…
                    </p>
                  ) : !customFields.length ? (
                    <p className="text-sm text-gray-500 text-center">
                      Nenhum campo cadastrado para este funil.
                    </p>
                  ) : !requiredCustomFieldsList.length ? (
                    <p className="text-sm text-gray-500 text-center">
                      Nenhum campo marcado como{" "}
                      <span className="font-medium">obrigatório</span> nesta
                      etapa ainda.
                    </p>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={requiredCustomFieldsList.map((f) => String(f.id))}
                        strategy={verticalListSortingStrategy}
                      >
                        <ul className="space-y-2">
                          {requiredCustomFieldsList.map((f, idx) => (
                            <SortableFieldRow
                              key={f.id}
                              field={f}
                              index={idx}
                              total={requiredCustomFieldsList.length}
                              onReorder={handleReorder}
                              onRemove={handleRemoveFieldFromStage}
                              disabled={savingRequiredFields}
                            />
                          ))}
                        </ul>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </div>
            )}

            <div className="h-2" />
          </div>
        </div>
      </div>
    </Modal>
  );
}
